import { useState } from 'react';

/**
 * Geolocation configuration options
 */
interface GeolocationOptions {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
}

/**
 * Raw coordinates from browser Geolocation API
 */
interface GeolocationCoords {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
}

/**
 * Complete geolocation result with timestamp
 */
interface GeolocationResult {
    coords: GeolocationCoords | null;
    timestamp: Date | null;
    error: string | null;
}

/**
 * Location data formatted for timer APIs (as strings)
 */
interface LocationStrings {
    lat: string;
    lon: string;
}

/**
 * Location data formatted for observations (as numbers with metadata)
 */
interface LocationObservation {
    lat: number;
    lng: number;
    accuracy: number;
    timestamp: Date;
}

/**
 * Hook return interface
 */
interface UseGeolocationReturn {
    getCurrentLocation: (options?: GeolocationOptions) => Promise<GeolocationResult>;
    getLocationAsStrings: (options?: GeolocationOptions) => Promise<LocationStrings | null>;
    getLocationForObservation: (options?: GeolocationOptions) => Promise<LocationObservation | null>;
    isLoading: boolean;
    error: string | null;
    isSupported: boolean;
}

/**
 * Default geolocation options used across the app
 */
const DEFAULT_OPTIONS: GeolocationOptions = {
    enableHighAccuracy: true,
    timeout: 15000,        // 15 seconds (increased for better reliability)
    maximumAge: 60000      // 1 minute cache
};

/**
 * Fallback options with lower accuracy for quicker response
 */
const FALLBACK_OPTIONS: GeolocationOptions = {
    enableHighAccuracy: false,
    timeout: 10000,        // 10 seconds
    maximumAge: 300000     // 5 minute cache (more lenient)
};

/**
 * Custom hook for consistent geolocation access across the application
 * 
 * Provides three methods for different use cases:
 * - getLocationAsStrings: For timer APIs (returns lat/lon as strings)
 * - getLocationForObservation: For image capture (returns lat/lng as numbers with metadata)
 * - getCurrentLocation: For raw coordinate access
 * 
 * @example
 * const { getLocationAsStrings, isLoading } = useGeolocation();
 * 
 * const location = await getLocationAsStrings();
 * if (location) {
 *   console.log(location.lat, location.lon);
 * }
 */
export const useGeolocation = (): UseGeolocationReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

    /**
     * Get current location with raw coordinates, with automatic retry fallback
     */
    const getCurrentLocation = async (
        options: GeolocationOptions = DEFAULT_OPTIONS,
        isRetry: boolean = false
    ): Promise<GeolocationResult> => {
        setIsLoading(true);
        setError(null);

        if (!isSupported) {
            const errorMsg = 'Geolocation is not supported by this browser';
            setError(errorMsg);
            setIsLoading(false);
            return { coords: null, timestamp: null, error: errorMsg };
        }

        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const result: GeolocationResult = {
                        coords: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            altitude: position.coords.altitude,
                            altitudeAccuracy: position.coords.altitudeAccuracy,
                            heading: position.coords.heading,
                            speed: position.coords.speed,
                        },
                        timestamp: new Date(position.timestamp),
                        error: null,
                    };
                    setIsLoading(false);
                    setError(null);
                    resolve(result);
                },
                async (error) => {
                    // Error codes: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
                    const errorCode = error.code;
                    const errorMsg = error.message || 'Failed to get location';
                    
                    console.warn(`Geolocation error (code ${errorCode}):`, errorMsg);

                    // If position unavailable or timeout, and not already a retry, try with lower accuracy
                    if (!isRetry && (errorCode === 2 || errorCode === 3)) {
                        console.log('Retrying with fallback options (lower accuracy)...');
                        const fallbackResult = await getCurrentLocation(FALLBACK_OPTIONS, true);
                        resolve(fallbackResult);
                    } else {
                        // Provide user-friendly error messages
                        let userMessage = errorMsg;
                        if (errorCode === 1) {
                            userMessage = 'Location permission denied. Please enable location services.';
                        } else if (errorCode === 2) {
                            userMessage = 'Location unavailable. Please check your GPS/WiFi and try again.';
                        } else if (errorCode === 3) {
                            userMessage = 'Location request timed out. Please try again.';
                        }
                        
                        setError(userMessage);
                        setIsLoading(false);
                        resolve({ coords: null, timestamp: null, error: userMessage });
                    }
                },
                {
                    enableHighAccuracy: options.enableHighAccuracy ?? DEFAULT_OPTIONS.enableHighAccuracy,
                    timeout: options.timeout ?? DEFAULT_OPTIONS.timeout,
                    maximumAge: options.maximumAge ?? DEFAULT_OPTIONS.maximumAge,
                }
            );
        });
    };

    /**
     * Get location formatted for timer APIs (lat/lon as strings)
     * Returns null if geolocation fails
     */
    const getLocationAsStrings = async (
        options: GeolocationOptions = DEFAULT_OPTIONS
    ): Promise<LocationStrings | null> => {
        const result = await getCurrentLocation(options);

        if (result.coords) {
            return {
                lat: result.coords.latitude.toString(),
                lon: result.coords.longitude.toString(),
            };
        }

        return null;
    };

    /**
     * Get location formatted for observations (lat/lng as numbers with metadata)
     * Returns null if geolocation fails
     */
    const getLocationForObservation = async (
        options: GeolocationOptions = DEFAULT_OPTIONS
    ): Promise<LocationObservation | null> => {
        const result = await getCurrentLocation(options);

        if (result.coords && result.timestamp) {
            return {
                lat: result.coords.latitude,
                lng: result.coords.longitude,
                accuracy: result.coords.accuracy,
                timestamp: result.timestamp,
            };
        }

        return null;
    };

    return {
        getCurrentLocation,
        getLocationAsStrings,
        getLocationForObservation,
        isLoading,
        error,
        isSupported,
    };
};

export default useGeolocation;
